<template>
    <div class="container" v-loading="loading">
        <div class="back" @click="goBack"><i class="el-icon-back"></i> 返回</div>
        <div v-if="info.code" class="header">
            <strong class="title">{{ info.name }}</strong>
            <code class="code">{{ info.code }}</code>
            <el-tag size="small" :type="statusTagType(info.status)">{{ statusText(info.status) }}</el-tag>
            <el-tag size="small" type="success">试用 {{ info.trial_days || 0 }} 天</el-tag>
            <span class="meta">创建 {{ formatTime(info.create_time) }} · 更新 {{ formatTime(info.update_time) }}</span>
        </div>
        <el-alert v-if="info.code && !productModels.length" class="model-warning" type="warning" show-icon :closable="false"
            title="该套餐尚未关联设备型号，App 端无法查到，请在「型号关联」中配置。"></el-alert>

        <el-tabs v-if="info.code" v-model="activeTab">
            <el-tab-pane label="基础信息" name="base">
                <el-form ref="baseForm" :model="baseForm" :rules="baseRules" label-width="140px" class="package-form">
                    <BaseInfoFields :form="baseForm"></BaseInfoFields>
                    <el-form-item>
                        <el-button type="primary" :loading="baseSaving" @click="saveBase">保存基础信息</el-button>
                    </el-form-item>
                </el-form>
            </el-tab-pane>

            <el-tab-pane label="价格管理" name="price">
                <template>
                    <h4 class="section-title">币种在售状态</h4>
                    <el-table border :data="savedPrices" size="small" class="price-table">
                        <el-table-column prop="currency" label="币种" width="100"></el-table-column>
                        <el-table-column prop="price" label="原价" width="120"></el-table-column>
                        <el-table-column prop="sale_price" label="售价" width="120"></el-table-column>
                        <el-table-column prop="price_id" label="Chargebee price_id" min-width="260"></el-table-column>
                        <el-table-column label="在售" width="100">
                            <!-- el-table 会用空 row（$index=-1）预渲染插槽，此时 status 非 0/1 会让 el-switch 自动触发 change，需跳过 -->
                            <template v-slot="scope">
                                <el-switch v-if="scope.$index >= 0" :model-value="scope.row.status" :active-value="1" :inactive-value="0"
                                    :disabled="priceStatusLoading === scope.row.currency"
                                    @change="changePriceStatus(scope.row, $event)"></el-switch>
                            </template>
                        </el-table-column>
                    </el-table>

                    <h4 class="section-title">编辑价格（全量替换）</h4>
                    <PriceEditor v-model="prices"></PriceEditor>
                    <div class="actions">
                        <el-button type="primary" size="small" :loading="priceSaving" @click="savePrices">保存完整价格列表</el-button>
                    </div>
                    <div class="help-text">保存采用全量替换：列表中删除的币种也会从服务端删除。价格变更不影响已有订阅，只影响新订阅。临时停售请使用上方「在售」开关。</div>
                </template>
            </el-tab-pane>

            <el-tab-pane label="权益特性" name="feature">
                <FeatureEditor v-model="features" @remove="removeFeature"></FeatureEditor>
                <div class="actions">
                    <el-button type="primary" size="small" :loading="featureSaving" @click="saveFeatures">保存权益（全量替换）</el-button>
                </div>
                <div class="help-text">保存采用全量替换：未提交的权益项会被删除。</div>
            </el-tab-pane>

            <el-tab-pane label="型号关联" name="model">
                <el-form :inline="true" size="small">
                    <el-form-item label="可售设备型号">
                        <SearchInnerModel v-model="selectedModels" :multiple="true" placeholder="选择设备型号，可多选" style="width: 420px"></SearchInnerModel>
                    </el-form-item>
                    <el-form-item>
                        <el-button type="primary" :loading="modelSaving" @click="saveModels">保存型号（全量替换）</el-button>
                    </el-form-item>
                </el-form>
                <div class="help-text model-help">保存后未勾选的已有型号会被解除关联；上架中的套餐不能清空型号，也不能删除最后一个型号，需先下架。</div>
                <el-table border :data="models" size="small">
                    <el-table-column prop="product_code" label="产品编号"></el-table-column>
                    <el-table-column prop="product_model_code" label="产品型号编号"></el-table-column>
                    <el-table-column label="操作" width="110">
                        <template v-slot="scope">
                            <el-button type="danger" size="mini" @click="deleteModel(scope.row)">解除关联</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <el-pagination v-model:currentPage="modelPage" :page-size="modelLimit" :total="modelTotal"
                    layout="total, prev, pager, next" @current-change="loadModels"></el-pagination>
            </el-tab-pane>

            <el-tab-pane label="上架区域" name="region">
                <el-form label-width="90px" size="small" style="max-width: 620px">
                    <el-form-item label="上架区域">
                        <el-select v-model="regions" multiple filterable allow-create default-first-option
                            placeholder="请选择或输入区域码" style="width: 420px">
                            <el-option v-for="region in regionOptions" :key="region" :label="region" :value="region"></el-option>
                        </el-select>
                        <div class="help-text">全量替换；清空后 App 端查不到该套餐，等效于全区下架。</div>
                    </el-form-item>
                    <el-form-item>
                        <el-button type="primary" :loading="regionSaving" @click="saveRegions">保存上架区域</el-button>
                    </el-form-item>
                </el-form>
            </el-tab-pane>
        </el-tabs>
    </div>
</template>

<script>
import api from '../../../axios/pack'
import SearchInnerModel from '../../../components/device/SearchInnerModel.vue'
import BaseInfoFields from './BaseInfoFields.vue'
import PriceEditor from './PriceEditor.vue'
import FeatureEditor from './FeatureEditor.vue'
import {
    REGION_OPTIONS, statusText, formatTime, baseRules, basePayload,
    validatePriceList, validateFeatureList, formatPrices, formatFeatures
} from './constants'

export default {
    name: 'PetPackageDetail',
    components: { SearchInnerModel, BaseInfoFields, PriceEditor, FeatureEditor },
    data() {
        return {
            regionOptions: REGION_OPTIONS,
            packageCode: '',
            info: {},
            baseForm: {},
            baseRules: baseRules(),
            savedPrices: [],
            prices: [],
            features: [],
            regions: [],
            models: [],
            activeTab: 'base',
            loading: false,
            baseSaving: false,
            priceSaving: false,
            priceStatusLoading: '',
            featureSaving: false,
            regionSaving: false,
            modelSaving: false,
            productModels: [],
            selectedModels: [],
            modelPage: 1,
            modelLimit: 20,
            modelTotal: 0
        }
    },
    methods: {
        statusText,
        formatTime,
        statusTagType(status) {
            return { 0: 'info', 1: 'success', 2: 'warning', 3: 'danger' }[status] || 'info'
        },
        loadDetail() {
            this.loading = true
            api.PetPackageDetail({ code: this.packageCode }).then(res => {
                if (res.data.code == 200) {
                    const data = res.data.data || {}
                    this.info = data.info || {}
                    this.baseForm = {
                        ...this.info,
                        service_time: this.info.time,
                        service_time_unit: this.info.time_unit
                    }
                    this.savedPrices = data.prices || []
                    this.prices = this.savedPrices.map(item => ({
                        currency: item.currency,
                        price: Number(item.price),
                        sale_price: Number(item.sale_price)
                    }))
                    this.features = (data.ext_features || []).map(item => ({
                        id: item.id,
                        item_key: item.item_key,
                        item_value: item.item_value,
                        sort_order: item.sort_order
                    }))
                    this.regions = data.regions || []
                    return
                }
                this.$message.error('获取套餐详情失败：' + res.data.msg)
            }).finally(() => { this.loading = false })
        },
        saveBase() {
            this.$refs.baseForm.validate(valid => {
                if (!valid) return
                this.baseSaving = true
                api.PetPackageUpdate({ code: this.packageCode, ...basePayload(this.baseForm) }).then(res => {
                    if (res.data.code == 200) {
                        this.$message.success('基础信息保存成功')
                        this.loadDetail()
                        return
                    }
                    this.$message.error('保存失败：' + res.data.msg)
                }).finally(() => { this.baseSaving = false })
            })
        },
        savePrices() {
            const message = validatePriceList(this.prices)
            if (message) return this.$message.error(message)
            const nextCurrencies = this.prices.map(item => item.currency.toUpperCase())
            const removed = this.savedPrices.map(item => item.currency).filter(item => !nextCurrencies.includes(item))
            const submit = () => {
                this.priceSaving = true
                api.PetPackageUpdatePrice({ package_code: this.packageCode, prices: formatPrices(this.prices) }).then(res => {
                    if (res.data.code == 200) {
                        this.$message.success('价格保存成功')
                        this.loadDetail()
                        return
                    }
                    this.$message.error('价格保存失败：' + res.data.msg)
                }).finally(() => { this.priceSaving = false })
            }
            if (removed.length) {
                this.$confirm(`以下币种将被删除（下线）：${removed.join(', ')}，确定继续?`, '提示', { type: 'warning' }).then(submit).catch(() => {})
                return
            }
            submit()
        },
        changePriceStatus(row, status) {
            const currency = String(row.currency || '').toUpperCase()
            if (!currency) return
            this.priceStatusLoading = currency
            api.PetPackagePriceStatus({ package_code: this.packageCode, currency, status: Number(status) }).then(res => {
                if (res.data.code == 200) {
                    this.$message.success(status === 1 ? '已恢复在售' : '已停售')
                    row.status = status
                    return
                }
                this.$message.error('操作失败：' + res.data.msg)
            }).finally(() => { this.priceStatusLoading = '' })
        },
        removeFeature(item, index) {
            // 已落库的单条特性直接调用删除接口，未保存的仅从表单移除
            if (!item.id) return this.features.splice(index, 1)
            this.$confirm(`确定删除权益 ${item.item_key}?`, '提示', { type: 'warning' }).then(() => {
                api.PetPackageFeatureDelete({ id: item.id }).then(res => {
                    if (res.data.code == 200) {
                        this.$message.success('已删除')
                        this.features.splice(index, 1)
                        return
                    }
                    this.$message.error('删除失败：' + res.data.msg)
                })
            }).catch(() => {})
        },
        loadFeatures() {
            api.PetPackageFeatureList({ package_code: this.packageCode }).then(res => {
                if (res.data.code == 200) {
                    this.features = ((res.data.data && res.data.data.data) || []).map(item => ({
                        id: item.id,
                        item_key: item.item_key,
                        item_value: item.item_value,
                        sort_order: item.sort_order
                    }))
                    return
                }
                this.$message.error('获取权益失败：' + res.data.msg)
            })
        },
        saveFeatures() {
            if (!this.features.length) return this.$message.error('权益列表不能为空')
            const message = validateFeatureList(this.features)
            if (message) return this.$message.error(message)
            this.featureSaving = true
            api.PetPackageFeatureUpdate({ package_code: this.packageCode, features: formatFeatures(this.features) }).then(res => {
                if (res.data.code == 200) {
                    this.$message.success('权益保存成功')
                    this.loadFeatures()
                    return
                }
                this.$message.error('权益保存失败：' + res.data.msg)
            }).finally(() => { this.featureSaving = false })
        },
        saveRegions() {
            const submit = () => {
                this.regionSaving = true
                api.PetPackageUpdateRegion({
                    code: this.packageCode,
                    regions: this.regions.map(item => item.toLowerCase())
                }).then(res => {
                    if (res.data.code == 200) {
                        this.$message.success('上架区域保存成功')
                        return
                    }
                    this.$message.error('上架区域保存失败：' + res.data.msg)
                }).finally(() => { this.regionSaving = false })
            }
            if (!this.regions.length) {
                this.$confirm('清空区域后 App 端将查不到该套餐（等效全区下架），确定继续?', '提示', { type: 'warning' }).then(submit).catch(() => {})
                return
            }
            submit()
        },
        loadModels() {
            api.PetPackageModelList({ package_code: this.packageCode, page: this.modelPage, limit: this.modelLimit }).then(res => {
                if (res.data.code == 200) {
                    this.models = res.data.data.data || []
                    this.modelTotal = res.data.data.page_info ? res.data.data.page_info.total : 0
                    return
                }
                this.$message.error('获取型号关联失败：' + res.data.msg)
            })
        },
        loadProductModels() {
            api.PetPackageModelGet({ package_code: this.packageCode }).then(res => {
                if (res.data.code == 200) {
                    this.productModels = (res.data.data && res.data.data.product_models) || []
                    this.selectedModels = [...this.productModels]
                    return
                }
                this.$message.error('获取设备型号失败：' + res.data.msg)
            })
        },
        refreshModels() {
            this.modelPage = 1
            this.loadModels()
            this.loadProductModels()
        },
        saveModels() {
            const removed = this.productModels.filter(item => !this.selectedModels.includes(item))
            const submit = () => {
                this.modelSaving = true
                api.PetPackageModelUpdate({ package_code: this.packageCode, product_models: this.selectedModels }).then(res => {
                    if (res.data.code == 200) {
                        this.$message.success('设备型号保存成功')
                        this.refreshModels()
                        return
                    }
                    this.$message.error('设备型号保存失败：' + res.data.msg)
                }).finally(() => { this.modelSaving = false })
            }
            if (!this.selectedModels.length) {
                this.$confirm('清空型号后 App 端将查不到该套餐，确定继续?', '提示', { type: 'warning' }).then(submit).catch(() => {})
                return
            }
            if (removed.length) {
                this.$confirm(`以下型号将被解除关联：${removed.join(', ')}，确定继续?`, '提示', { type: 'warning' }).then(submit).catch(() => {})
                return
            }
            submit()
        },
        deleteModel(row) {
            this.$confirm(`确定解除型号 ${row.product_model_code} 的关联?`, '提示', { type: 'warning' }).then(() => {
                api.PetPackageModelDelete({ package_code: this.packageCode, product_model_code: row.product_model_code }).then(res => {
                    if (res.data.code == 200) {
                        this.$message.success('已解除关联')
                        this.refreshModels()
                        return
                    }
                    this.$message.error('解除关联失败：' + res.data.msg)
                })
            }).catch(() => {})
        },
        goBack() { this.$router.push('/iot/petpack') }
    },
    created() {
        this.packageCode = this.$route.params.code
        this.loadDetail()
        this.loadModels()
        this.loadProductModels()
    }
}
</script>

<style scoped>
.back { display: inline-flex; align-items: center; gap: 4px; margin-bottom: 16px; color: #606266; cursor: pointer; font-size: 14px; }
.back:hover { color: #409eff; }
.header { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; padding: 12px 16px; border: 1px solid #e7ebf3; border-radius: 8px; background: #f8fbff; }
.title { color: #303133; font-size: 17px; }
.code { padding: 2px 7px; border-radius: 4px; background: #eef3f9; color: #606266; font-family: monospace; font-size: 12px; }
.meta { color: #a0a4ab; font-size: 12px; }
.model-warning { margin-top: 12px; }
.model-help { margin: 0 0 12px; }
.package-form { max-width: 960px; }
.section-title { margin: 8px 0 12px; color: #303133; font-size: 14px; }
.price-table { margin-bottom: 20px; }
.actions { margin-top: 12px; }
.help-text, .empty-tip { margin-top: 10px; color: #909399; font-size: 12px; }
</style>
